import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckIcon from "@material-ui/icons/Check";
import TrapFocus from "@material-ui/core/Unstable_TrapFocus";
import api from "../../services/api";
import {
  emptySmartPrompt,
  escapeSmartText,
  smartContentToText,
  smartPromptErrors,
  smartTextToContent,
  smartValue,
  usedSmartKeys,
} from "../../helpers/smartPrompt";
import { createSmartValueExtension } from "./SmartValueExtension";

const useStyles = makeStyles((theme) => ({
  root: { minWidth: 0 },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    alignItems: "center",
    marginBottom: 10,
  },
  title: { marginRight: "auto" },
  viewSelector: {
    display: "inline-flex",
    gap: 2,
    padding: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    background: theme.palette.action.hover,
  },
  viewButton: {
    borderRadius: 7,
    padding: "5px 10px",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "none",
    "& .MuiButton-startIcon": { visibility: "hidden" },
    "&[aria-pressed='true']": {
      background: theme.palette.primary.dark,
      color: theme.palette.getContrastText(theme.palette.primary.dark),
      fontWeight: 700,
      "& .MuiButton-startIcon": { visibility: "visible" },
      "&:hover": { background: theme.palette.primary.dark },
    },
    "&.Mui-focusVisible": {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
  },
  focused: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.modal - 1,
    background: theme.palette.background.paper,
    padding: 20,
    overflowY: "auto",
  },
  document: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    "& .tiptap": {
      padding: 16,
      minHeight: 384,
      outline: "none",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
      fontSize: 14,
      lineHeight: "22px",
      color: theme.palette.text.primary,
    },
    "& .tiptap:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
      borderRadius: 10,
    },
    "& p": { margin: 0, minHeight: 22 },
    "& button[data-smart-value]": {
      display: "inline",
      verticalAlign: "baseline",
      maxWidth: "100%",
      overflowWrap: "anywhere",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 5,
      padding: "1px 6px",
      margin: "0 2px",
      font: "inherit",
      color: theme.palette.text.primary,
      background: alpha(theme.palette.primary.main, 0.07),
      cursor: "pointer",
    },
    "& button[data-invalid='true']": { borderColor: theme.palette.error.main },
    "& .ProseMirror-selectednode, & button:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  preview: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    minHeight: 384,
    padding: 16,
    margin: 0,
    font: "inherit",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
  },
  hint: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "space-between",
    marginTop: 8,
  },
  list: { display: "flex", flexDirection: "column", gap: 8, marginTop: 16 },
  fields: { display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 },
  review: {
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    fontFamily: "inherit",
    maxHeight: "30vh",
    overflowY: "auto",
  },
}));

const emptyCompanyValues = [];

export default function SmartPromptEditor({
  value = emptySmartPrompt(),
  onChange,
  disabled = false,
  companyName = "",
  companyValues = emptyCompanyValues,
  templateDocument,
  admin = false,
  error,
}) {
  const classes = useStyles();
  const [fieldId] = useState(
    () => `smart-${Math.random().toString(36).slice(2)}`
  );
  const latest = useRef({ value, onChange, disabled });
  latest.current = { value, onChange, disabled };
  const [focused, setFocused] = useState(false);
  const [filled, setFilled] = useState(false);
  const [preview, setPreview] = useState({
    text: "",
    errors: [],
    loading: false,
    failed: false,
  });
  const [picker, setPicker] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftError, setDraftError] = useState("");
  const [restore, setRestore] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [previewRetry, setPreviewRetry] = useState(0);
  const editorRef = useRef(null);
  const rootRef = useRef(null);
  const focusSelection = useRef(null);
  const usedKeys = usedSmartKeys(value.text);
  const uniqueKeys = [...new Set(usedKeys)];
  const localErrors = smartPromptErrors(value, companyName, 0, companyValues);
  const openEditRef = useRef(null);
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        bold: false,
        italic: false,
        strike: false,
        code: false,
        codeBlock: false,
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        dropcursor: false,
        gapcursor: false,
      }),
      createSmartValueExtension(
        (key) => latest.current.value.variables.find((v) => v.key === key),
        (key, position) => openEditRef.current(key, position)
      ),
    ],
    []
  );
  const editor = useEditor({
    extensions,
    content: smartTextToContent(value.text),
    editable: !disabled,
    editorProps: {
      attributes: {
        role: "textbox",
        "aria-label": "Instruções",
        "aria-multiline": "true",
        "data-field": "promptDocument",
      },
      clipboardTextSerializer: (slice) =>
        slice.content.textBetween(0, slice.content.size, "\n", (node) =>
          node.type.name === "smartValue"
            ? `{{${node.attrs.variableKey}}}`
            : "\n"
        ),
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain");
        if (text === undefined || latest.current.disabled) return false;
        event.preventDefault();
        editorRef.current?.commands.insertContent(
          smartTextToContent(text).content
        );
        return true;
      },
      handleKeyDown(view, event) {
        if (
          event.key === "Enter" &&
          view.state.selection.node?.type.name === "smartValue"
        ) {
          openEditRef.current(
            view.state.selection.node.attrs.variableKey,
            view.state.selection.from
          );
          return true;
        }
        return false;
      },
    },
    onSelectionUpdate({ editor: current }) {
      const { from, to } = current.state.selection;
      setSelectedText(
        from === to
          ? ""
          : current.state.doc.textBetween(
              from,
              to,
              "\n",
              (node) => `{{${node.attrs.variableKey}}}`
            )
      );
    },
    onUpdate({ editor: current }) {
      const text = smartContentToText(current.getJSON());
      const next = { ...latest.current.value, text };
      latest.current.value = next;
      latest.current.onChange(next);
      const selection = current.state.selection;
      if (
        selection.empty &&
        selection.$from.parent.textBetween(
          Math.max(0, selection.$from.parentOffset - 2),
          selection.$from.parentOffset
        ) === "{{"
      ) {
        setSearch("");
        setPicker({
          mode: "insert",
          range: { from: selection.from - 2, to: selection.from },
        });
      }
    },
  });
  editorRef.current = editor;
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled, false);
    if (smartContentToText(editor.getJSON()) !== value.text)
      editor.commands.setContent(smartTextToContent(value.text), false);
    editor.view.dom
      .querySelectorAll("button[data-smart-value]")
      .forEach((button) => {
        const key = button.dataset.smartValue;
        const variable = value.variables.find((v) => v.key === key);
        button.textContent = variable?.label || key;
        button.setAttribute(
          "aria-label",
          `Editar variável ${variable?.label || key}`
        );
        button.dataset.invalid = String(
          localErrors.some((item) => item.key === key)
        );
        button.disabled = disabled;
      });
  }, [editor, disabled, value, companyName, companyValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focused) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [focused]);
  useEffect(() => {
    if (!filled) return undefined;
    let active = true;
    setPreview({ text: "", errors: [], loading: true, failed: false });
    const timer = setTimeout(async () => {
      try {
        // Administrative preview uses a fictional company, never the administrator's tenant data.
        const document = admin
          ? {
              ...value,
              values: {
                ...value.values,
                ...Object.fromEntries(
                  value.variables
                    .filter((v) => v.source === "company.name")
                    .map((v) => [v.key, "Empresa de exemplo"])
                ),
              },
            }
          : value;
        const { data } = await api.post("/prompt/values-preview", {
          promptDocument: document,
        });
        if (active)
          setPreview({
            text: data.resolvedText,
            errors: data.errors || [],
            loading: false,
            failed: false,
          });
      } catch {
        if (active)
          setPreview({ text: "", errors: [], loading: false, failed: true });
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [filled, value, admin, previewRetry, companyValues]);

  const focusEditor = () =>
    requestAnimationFrame(() => {
      if (editor && !editor.isDestroyed) editor.commands.focus();
    });
  const openEdit = (key, position = null) => {
    const variable = value.variables.find((v) => v.key === key);
    if (!variable || disabled) return;
    setEditing({ key, position, isNew: false });
    setDraft({
      ...variable,
      source: variable.source || "agent",
      optionsText: (variable.options || []).join("\n"),
      override: Object.prototype.hasOwnProperty.call(value.values, key),
      value: smartValue(value, variable, companyName, companyValues),
    });
    setDraftError("");
  };
  openEditRef.current = openEdit;
  const addNew = () => {
    setEditing({
      isNew: true,
      range: {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      },
    });
    setDraft({
      key: "",
      label: "",
      type: "text",
      source: "agent",
      required: true,
      optionsText: "",
      override: true,
      value: selectedText,
    });
    setDraftError("");
  };
  const applyDraft = () => {
    const key = draft.key || `valor_${Date.now().toString(36)}`;
    const definition = {
      key,
      label: draft.label.trim(),
      type: draft.type,
      required: draft.required,
      source: draft.source,
      ...(draft.source === "company.variable" ? { companyVariableId: draft.companyVariableId } : {}),
      ...(draft.description ? { description: draft.description } : {}),
      ...(draft.type === "select"
        ? {
            options: draft.optionsText
              .split("\n")
              .map((v) => v.trim())
              .filter(Boolean),
          }
        : {}),
      ...(admin && draft.source !== "company.name"
        ? { default: draft.value }
        : {}),
    };
    if (draft.source === "company.variable" && !companyValues.some(v => v.id === draft.companyVariableId)) {
      setDraftError("Selecione uma variável desta empresa."); return;
    }
    if (definition.label.length < 2 || definition.label.length > 100) {
      setDraftError("Use um nome entre 2 e 100 caracteres.");
      return;
    }
    if (definition.type === "select" && definition.options.length < 2) {
      setDraftError("Informe ao menos duas opções.");
      return;
    }
    if (editing.isNew && value.variables.length >= 30) {
      setDraftError("Use no máximo 30 variáveis.");
      return;
    }
    const next = {
      ...value,
      variables: editing.isNew
        ? [...value.variables, definition]
        : value.variables.map((v) => (v.key === key ? definition : v)),
      values: { ...value.values },
    };
    if (["company.name", "company.variable"].includes(definition.source) && !draft.override)
      delete next.values[key];
    else next.values[key] = draft.value;
    latest.current = { ...latest.current, value: next };
    onChange(next);
    if (editing.isNew)
      editor
        .chain()
        .focus()
        .insertContentAt(editing.range, {
          type: "smartValue",
          attrs: { variableKey: key },
        })
        .run();
    setEditing(null);
    focusEditor();
  };
  const insert = (variable) => {
    const range = picker.range;
    if (!value.variables.some(v => v.key === variable.key)) {
      const next = { ...value, variables: [...value.variables, variable] };
      latest.current.value = next;
      onChange(next);
    }
    setPicker(null);
    editor
      .chain()
      .focus()
      .insertContentAt(range, {
        type: "smartValue",
        attrs: { variableKey: variable.key },
      })
      .run();
  };
  const locate = (key) => {
    let position = null;
    editor.state.doc.descendants((node, pos) => {
      if (
        position === null &&
        node.type.name === "smartValue" &&
        node.attrs.variableKey === key
      )
        position = pos;
    });
    setPicker(null);
    setFilled(false);
    if (position !== null) {
      editor.chain().focus().setNodeSelection(position).scrollIntoView().run();
      openEdit(key, position);
    }
  };
  const toggleFocus = () => {
    focusSelection.current = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
      scroll: window.scrollY,
    };
    setFocused((current) => !current);
    requestAnimationFrame(() => {
      editor.commands.focus(undefined, { scrollIntoView: false });
      editor.commands.setTextSelection({
        from: focusSelection.current.from,
        to: focusSelection.current.to,
      });
    });
  };
  const dialogOpen = Boolean(picker || editing || restore);
  const label = "Instruções";
  return (
    <>
      <TrapFocus
        open={focused && !dialogOpen}
        getDoc={() => document}
        isEnabled={() => true}
        disableRestoreFocus
      >
        <div
          ref={rootRef}
          className={`${classes.root} ${focused ? classes.focused : ""}`}
          role={focused ? "dialog" : undefined}
          aria-modal={focused ? true : undefined}
          aria-label={focused ? label : undefined}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === "Escape" && focused && !dialogOpen) {
              event.stopPropagation();
              toggleFocus();
            }
          }}
        >
          <div className={classes.toolbar}>
            <Typography className={classes.title}>{label}</Typography>
            <div
              className={classes.viewSelector}
              role="group"
              aria-label="Visualização das instruções"
            >
              <Button
                className={classes.viewButton}
                size="small"
                type="button"
                startIcon={<CheckIcon />}
                aria-pressed={!filled}
                onClick={() => setFilled(false)}
              >
                Editar
              </Button>
              <Button
                className={classes.viewButton}
                size="small"
                type="button"
                startIcon={<CheckIcon />}
                aria-pressed={filled}
                onClick={() => setFilled(true)}
              >
                Ver preenchido
              </Button>
            </div>
            <Button
              size="small"
              type="button"
              onClick={toggleFocus}
              disabled={!editor}
            >
              {focused ? "Sair do modo de foco" : "Modo de foco"}
            </Button>
          </div>
          {!filled && (
            <div className={classes.toolbar}>
              <Button
                size="small"
                type="button"
                disabled={disabled || !editor}
                onClick={() => {
                  setSearch("");
                  setPicker({
                    mode: "insert",
                    range: {
                      from: editor.state.selection.from,
                      to: editor.state.selection.to,
                    },
                  });
                }}
              >
                Inserir variável
              </Button>
              <Button
                size="small"
                type="button"
                disabled={disabled || !selectedText || !editor}
                onClick={addNew}
              >
                Transformar em variável
              </Button>
              <Button
                size="small"
                type="button"
                disabled={disabled}
                onClick={() => {
                  setSearch("");
                  setPicker({ mode: "used" });
                }}
              >
                Variáveis usadas · {uniqueKeys.length}
                {localErrors.length ? ` (${localErrors.length} pendentes)` : ""}
              </Button>
              {templateDocument && (
                <Button
                  size="small"
                  type="button"
                  disabled={disabled}
                  onClick={() => setRestore(true)}
                >
                  Restaurar texto do template
                </Button>
              )}
            </div>
          )}
          <div hidden={filled} className={classes.document}>
            <EditorContent editor={editor} />
          </div>
          {filled && (
            <div aria-live="polite">
              {preview.loading ? (
                <Typography role="status">Atualizando prévia...</Typography>
              ) : preview.failed ? (
                <Alert
                  severity="error"
                  action={
                    <Button onClick={() => setPreviewRetry((n) => n + 1)}>
                      Tentar novamente
                    </Button>
                  }
                >
                  Não foi possível preencher as instruções.
                </Alert>
              ) : (
                <>
                  <pre className={classes.preview}>{preview.text}</pre>
                  {preview.errors.map((item, index) => (
                    <Typography color="error" key={index}>
                      {item.message}
                    </Typography>
                  ))}
                </>
              )}
            </div>
          )}
          <div className={classes.hint}>
            <Typography
              variant="caption"
              color={error ? "error" : "textSecondary"}
            >
              {error ||
                (admin
                  ? "Variáveis automáticas usam uma empresa fictícia na prévia."
                  : "Digite {{ para inserir uma variável. As alterações são aplicadas ao salvar o agente.")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {value.text.length.toLocaleString("pt-BR")} / 20.000
            </Typography>
          </div>
        </div>
      </TrapFocus>
      <Dialog
        open={Boolean(picker)}
        onClose={() => {
          setPicker(null);
          focusEditor();
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="smart-picker-title"
      >
        <DialogTitle id="smart-picker-title">
          {picker?.mode === "used"
            ? "Variáveis usadas"
            : "Inserir variável"}
        </DialogTitle>
        <DialogContent>
          <TextField
            id={`${fieldId}-search`}
            autoFocus
            fullWidth
            label="Buscar variável"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className={classes.list}>
            {value.variables
              .filter(
                (v) =>
                  (picker?.mode !== "used" || usedKeys.includes(v.key)) &&
                  `${v.label} ${v.key}`
                    .toLowerCase()
                    .includes(search.toLowerCase())
              )
              .map((variable) => (
                <Button
                  key={variable.key}
                  type="button"
                  style={{ justifyContent: "flex-start", textAlign: "left" }}
                  onClick={() =>
                    picker.mode === "used"
                      ? locate(variable.key)
                      : insert(variable)
                  }
                >
                  {variable.label}
                  {variable.source === "company.name" ? " · Empresa" : ""}
                  {variable.source === "company.variable" ? Object.prototype.hasOwnProperty.call(value.values, variable.key) ? " · Substituição local" : " · Compartilhado" : ""}
                  {picker?.mode === "used"
                    ? ` · ${
                        usedKeys.filter((k) => k === variable.key).length
                      } trecho(s)`
                    : ""}
                  {localErrors.some((item) => item.key === variable.key)
                    ? " · Pendente"
                    : ""}
                </Button>
              ))}
            {!admin && picker?.mode === "insert" && <>
              <Typography variant="subtitle2">Variáveis da empresa</Typography>
              {companyValues.filter(v => !value.variables.some(item => item.source === "company.variable" && item.companyVariableId === v.id) && `${v.label} ${v.key}`.toLowerCase().includes(search.toLowerCase())).map(shared => {
                const existing = value.variables.find(v => v.source === "company.variable" && v.companyVariableId === shared.id);
                let key = shared.key;
                let suffix = 2;
                const existingKeys = value.variables.map(v => v.key);
                while (!existing && existingKeys.includes(key)) key = `${shared.key.slice(0, 55)}_${suffix++}`;
                return <Button key={shared.id} style={{ justifyContent: "flex-start", textAlign: "left" }}
                  disabled={!existing && value.variables.length >= 30}
                  onClick={() => insert(existing || { key, label: shared.label, type: shared.type,
                    required: true, source: "company.variable", companyVariableId: shared.id,
                    ...(shared.options ? { options: shared.options } : {}) })}>
                  {shared.label} · Compartilhado
                </Button>;
              })}
              {!companyValues.length && <Typography variant="body2" color="textSecondary">Cadastre variáveis para reutilizá-las nos agentes desta empresa.</Typography>}
              <Button component="a" href="/prompts?section=values" target="_blank" rel="noopener noreferrer">Gerenciar variáveis da empresa ↗</Button>
            </>}
            {picker?.mode === "used" &&
              localErrors
                .filter(
                  (item) => !value.variables.some((v) => v.key === item.key)
                )
                .map((item) => (
                  <Typography key={item.key} color="error">
                    {item.message}
                  </Typography>
                ))}
            {!value.variables.length && (
              <Typography color="textSecondary">
                Nenhuma variável cadastrada.
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          {picker?.mode === "insert" && (
            <Button
              disabled={disabled}
              onClick={() => {
                const range = picker.range;
                setPicker(null);
                addNew();
                setEditing({ isNew: true, range });
              }}
            >
              Criar variável
            </Button>
          )}
          <Button
            onClick={() => {
              setPicker(null);
              focusEditor();
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(editing)}
        onClose={() => {
          setEditing(null);
          focusEditor();
        }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="smart-value-title"
      >
        <DialogTitle id="smart-value-title">
          {editing?.isNew
            ? "Criar variável"
            : "Editar variável"}
        </DialogTitle>
        <DialogContent>
          {draft && (
            <div className={classes.fields}>
              <TextField
                id={`${fieldId}-name`}
                autoFocus
                label="Nome da variável"
                value={draft.label}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
              <TextField
                id={`${fieldId}-source`}
                select
                label="Origem"
                value={draft.source}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    source: e.target.value,
                    type:
                      e.target.value === "company.name" ? "text" : draft.type,
                    override: e.target.value === "agent",
                    companyVariableId: undefined,
                    value:
                      e.target.value === "company.name"
                        ? companyName
                        : draft.value,
                  })
                }
              >
                <MenuItem value="agent">
                  {admin ? "Configurado no agente" : "Configurado neste agente"}
                </MenuItem>
                <MenuItem value="company.name">
                  Nome da empresa (cadastro)
                </MenuItem>
                {!admin && <MenuItem value="company.variable">Variáveis da empresa</MenuItem>}
              </TextField>
              {draft.source === "company.variable" && !admin && <>
                <Autocomplete options={companyValues} getOptionLabel={v => `${v.label} (${v.key})`}
                  getOptionSelected={(a, b) => a.id === b.id}
                  value={companyValues.find(v => v.id === draft.companyVariableId) || null}
                  onChange={(_event, shared) => setDraft({ ...draft, companyVariableId: shared?.id,
                    type: shared?.type || "text", optionsText: (shared?.options || []).join("\n"),
                    override: false, value: shared?.value ?? "" })}
                  renderInput={params => <TextField {...params} label="Variável da empresa" placeholder="Busque por nome ou chave" />} />
                <FormControlLabel label="Usar outro valor somente neste agente" control={<Checkbox checked={draft.override}
                  onChange={e => setDraft({ ...draft, override: e.target.checked,
                    value: e.target.checked ? draft.value : companyValues.find(v => v.id === draft.companyVariableId)?.value ?? "" })} />} />
                <Typography variant="body2" color="textSecondary">{draft.override
                  ? "Esta substituição vale apenas para este agente. O valor compartilhado será preservado."
                  : "O agente acompanha as atualizações desta variável na empresa."}</Typography>
                <Button component="a" href="/prompts?section=values" target="_blank" rel="noopener noreferrer">Gerenciar variáveis da empresa ↗</Button>
              </>}
              {draft.source === "company.name" && !admin && (
                <FormControlLabel
                  label="Usar outro nome neste agente"
                  control={
                    <Checkbox
                      checked={draft.override}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          override: e.target.checked,
                          value: e.target.checked ? draft.value : companyName,
                        })
                      }
                    />
                  }
                />
              )}
              {draft.source === "agent" && (
                <TextField
                  id={`${fieldId}-type`}
                  select
                  label="Tipo da variável"
                  value={draft.type}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      type: e.target.value,
                      value:
                        e.target.value === "boolean"
                          ? false
                          : String(draft.value),
                    })
                  }
                >
                  {[
                    ["text", "Texto curto"],
                    ["textarea", "Texto longo"],
                    ["number", "Número"],
                    ["boolean", "Sim ou não"],
                    ["select", "Lista de opções"],
                  ].map(([key, name]) => (
                    <MenuItem key={key} value={key}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {draft.type === "select" && draft.source !== "company.variable" && (
                <TextField
                  id={`${fieldId}-options`}
                  multiline
                  minRows={2}
                  label="Opções (uma por linha)"
                  value={draft.optionsText}
                  onChange={(e) =>
                    setDraft({ ...draft, optionsText: e.target.value })
                  }
                />
              )}
              {draft.type === "boolean" ? (
                <FormControlLabel
                  label={admin ? "Valor padrão" : "Valor"}
                  control={
                    <Checkbox
                      disabled={draft.source === "company.variable" && !draft.override}
                      checked={Boolean(draft.value)}
                      onChange={(e) =>
                        setDraft({ ...draft, value: e.target.checked })
                      }
                    />
                  }
                />
              ) : (
                <TextField
                  id={`${fieldId}-value`}
                  label={
                    admin
                      ? draft.source === "company.name"
                        ? "Valor de exemplo"
                        : "Valor padrão"
                      : "Valor"
                  }
                  multiline={draft.type === "textarea"}
                  minRows={draft.type === "textarea" ? 4 : undefined}
                  maxRows={10}
                  select={draft.type === "select"}
                  type={draft.type === "number" ? "number" : "text"}
                  disabled={
                    (draft.source === "company.name" && admin) ||
                    (["company.name", "company.variable"].includes(draft.source) && !draft.override)
                  }
                  value={
                    admin && draft.source === "company.name"
                      ? "Empresa de exemplo"
                      : draft.value
                  }
                  inputProps={{ maxLength: 5000 }}
                  onChange={(e) =>
                    setDraft({ ...draft, value: e.target.value })
                  }
                >
                  {draft.type === "select" &&
                    draft.optionsText
                      .split("\n")
                      .filter(Boolean)
                      .map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                </TextField>
              )}
              <FormControlLabel
                label="Obrigatório quando usado no prompt"
                control={
                  <Checkbox
                    checked={draft.required}
                    onChange={(e) =>
                      setDraft({ ...draft, required: e.target.checked })
                    }
                  />
                }
              />
              {!editing?.isNew && (
                <Typography variant="body2">
                  Usada em{" "}
                  {usedKeys.filter((key) => key === editing?.key).length}{" "}
                  trecho(s) {admin ? "deste template" : "deste agente"}.
                </Typography>
              )}
              {draftError && <Alert severity="error">{draftError}</Alert>}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {editing?.position !== null && editing?.position !== undefined && (
            <Button
              disabled={disabled}
              onClick={() => {
                const variable = value.variables.find(
                  (v) => v.key === editing.key
                );
                const text = escapeSmartText(
                  smartValue(value, variable, companyName, companyValues)
                );
                editor
                  .chain()
                  .focus()
                  .insertContentAt(
                    { from: editing.position, to: editing.position + 1 },
                    text ? { type: "text", text } : ""
                  )
                  .run();
                setEditing(null);
              }}
            >
              Converter em texto
            </Button>
          )}
          <Button
            onClick={() => {
              setEditing(null);
              focusEditor();
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={applyDraft}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={restore}
        onClose={() => setRestore(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="smart-restore-title"
      >
        <DialogTitle id="smart-restore-title">
          Restaurar texto do template?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Revise a substituição. Os valores compatíveis serão mantidos; as
            alterações só serão persistidas ao salvar o agente.
          </Typography>
          <Typography variant="subtitle2">Texto atual</Typography>
          <pre className={classes.review}>{value.text}</pre>
          <Typography variant="subtitle2">Texto do template</Typography>
          <pre className={classes.review}>{templateDocument?.text}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestore(false)}>
            Manter personalização
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const definitions = templateDocument.variables.map(
                (v) =>
                  value.variables.find((current) => current.key === v.key) || v
              );
              onChange({
                ...templateDocument,
                variables: definitions,
                values: Object.fromEntries(
                  Object.entries(value.values).filter(([key]) =>
                    definitions.some((v) => v.key === key)
                  )
                ),
              });
              setRestore(false);
              setFilled(false);
              focusEditor();
            }}
          >
            Restaurar texto
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
